import * as React from "react";
import { GetAlbumDetailsQuery, GetFaQsByEventQuery } from "@src/@types/api.d";
import { useFaqList } from "@src/hooks/useFaqList";
import { motion } from "framer-motion";

export type ITemplate10FAQ = {
  eventId: number;
  faqData: GetFaQsByEventQuery;
  albumDetailsData?: GetAlbumDetailsQuery;
  loading?: boolean;
};

const FAQ: React.FC<ITemplate10FAQ> = ({ eventId, faqData }) => {
  const faqSectionRef = React.useRef<HTMLDivElement>(null);
  const {
    faqItems,
    paginatedFaqItems,
    currentPage,
    totalPages,
    handlePageChange,
  } = useFaqList({ faqData, itemsPerPage: 10 });

  if (!faqItems.length) return null;

  const goToPage = (page: number) => {
    handlePageChange(page);
    setTimeout(() => {
      faqSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  return (
    <div
      ref={faqSectionRef}
      className="w-full overflow-hidden relative bg-[#FAF7F5] px-6 py-10 md:px-10 md:py-16"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { delayChildren: 0.1, staggerChildren: 0.1 },
          },
        }}
        className="relative mx-auto max-w-5xl"
      >
        <div className="mb-12 text-center md:mb-16">
          <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
            FAQ
          </span>
          <h2 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
            All You{" "}
            <em className="font-light italic text-[#228B22]">Need To Know</em>
          </h2>
          <div className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
          </div>
        </div>

        <div className="border-y border-[#228B22]/12">
          {paginatedFaqItems.map((item, index) => {
            const itemNumber = (currentPage - 1) * 10 + index + 1;

            return (
              <motion.article
                key={item.id}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.55 },
                  },
                }}
                className="grid gap-5 border-b border-[#228B22]/10 py-9 last:border-b-0 md:grid-cols-[72px_1fr] md:gap-8"
              >
                <div className="font-corGaramond text-5xl font-light leading-none text-[#228B22]/20">
                  {String(itemNumber).padStart(2, "0")}
                </div>

                <div>
                  <h3 className="mb-4 font-jost text-[0.72rem] font-medium uppercase leading-[1.8] tracking-[0.24em] text-[#228B22]">
                    {item.question}
                  </h3>
                  <p className="max-w-3xl whitespace-pre-line font-corGaramond text-xl font-light leading-[1.75] text-[#401d15]/70 md:text-2xl">
                    {item.answer}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            <button
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex h-[46px] w-[46px] items-center justify-center rounded-full border border-[#228B22] bg-transparent text-[#228B22] transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Previous page"
            >
              <svg
                className="h-4 w-4 rotate-180"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <span className="font-jost text-[0.58rem] font-medium uppercase tracking-[0.25em] text-[#401d15]/45">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex h-[46px] w-[46px] items-center justify-center rounded-full border border-[#228B22] bg-transparent text-[#228B22] transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Next page"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default FAQ;
